import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════
// SLD WALKTHROUGH  (extracted verbatim from the me-portal GTASLDViewer)
// A "GTA-style" single-line-diagram viewer: a map of pins (x/y %), click a
// pin to zoom-walk into it and play its before/after photos, arrow-navigate
// point to point, with a live mini-map tracker showing "you are here".
//
// Props:
//   sldData  = { diagram_url, points:[{ id, label, x_percent, y_percent }] }
//   photos   = [{ point_id, point_label, photo_type:'before'|'after', photo_url }]
//   baseUrl  = image host (SERVER_BASE, i.e. API base WITHOUT /api)
//   siteName = label shown on the map overlay
//   onViewChange(view) optional — fires with 'map' | 'photo'
// ═══════════════════════════════════════════════════════════════

const SLD_CSS = `
  .sldw-root .sld-image-container { position: relative; display: inline-block; max-width: 100%; max-height: 100%; }
  .sldw-root .gta-map-img { max-width: 100%; max-height: 600px; display: block; object-fit: contain; }
  .sldw-root .sv-mobile-hint { position:absolute; bottom:16px; left:50%; transform:translateX(-50%); color:rgba(255,255,255,0.38); font-size:11px; font-weight:600; background:rgba(0,0,0,0.58); padding:5px 14px; border-radius:20px; z-index:10; white-space:nowrap; pointer-events:none; }
  .sldw-root .sv-back-btn { position:absolute; top:18px; left:18px; z-index:40; display:flex; align-items:center; gap:7px; padding:8px 16px; border-radius:10px; background:rgba(0,0,0,0.70); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.18); color:#fff; font-weight:700; font-size:12px; cursor:pointer; transition:all 0.2s; }
  .sldw-root .sv-back-btn:hover { background:rgba(56,189,248,0.28); }
  .sldw-root .sv-bottom-bar { position:absolute; bottom:20px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:10px; z-index:40; background:rgba(0,0,0,0.75); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.12); padding:8px 18px; border-radius:12px; }
  .sldw-root .cp-fade{animation:sldwFade 0.4s ease both;}
  @keyframes sldwFade{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  @keyframes bobWalk { 0% { transform: translateY(0px); } 100% { transform: translateY(-4px); } }
  @keyframes swingLeg { 0% { transform: rotate(-35deg); } 100% { transform: rotate(35deg); } }
  @keyframes swingArm { 0% { transform: rotate(-30deg); } 100% { transform: rotate(30deg); } }
  @keyframes pinPulse{0%,100%{box-shadow:0 0 0 0 rgba(34,211,165,0.5);}60%{box-shadow:0 0 0 10px rgba(34,211,165,0);}}
  @keyframes fadeUp{from{opacity:0;transform:translateX(-50%) translateY(6px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}
  @media (max-width: 600px) {
    .sldw-root .gta-container { height: 450px !important; }
    .sldw-root .sv-back-btn span { display: none; }
  }
`;

const SLDWalkthrough = ({ photos = [], sldData, baseUrl = '', siteName, onViewChange }) => {
  const [view, setView]                 = useState('map');
  const [activePoint, setActivePoint]   = useState(null);
  const [photoType, setPhotoType]       = useState('before');
  const [navDir, setNavDir]             = useState('fwd');
  const [mapPhase, setMapPhase]         = useState('visible');
  const [photoPhase, setPhotoPhase]     = useState('hidden');
  const [zoomOrigin, setZoomOrigin]     = useState('50% 50%');
  const [hoveredPin, setHoveredPin]     = useState(null);
  const [hoveredArrow, setHoveredArrow] = useState(null);
  const [isWalking, setIsWalking]       = useState(false);

  const getUrl = (p) => {
    if (!p?.photo_url) return '';
    if (p.photo_url.startsWith('http')) return p.photo_url;
    return `${baseUrl}${p.photo_url.startsWith('/') ? '' : '/'}${p.photo_url}`;
  };

  useEffect(() => {
    photos?.forEach(p => { if (p.photo_url) { const i = new Image(); i.src = getUrl(p); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  const { pointList, diagramUrl } = useMemo(() => {
    let dUrl = sldData?.diagram_url || null;
    const pts = sldData?.points || [];
    if (dUrl && !dUrl.startsWith('http')) dUrl = `${baseUrl}${dUrl.startsWith('/') ? '' : '/'}${dUrl}`;
    return {
      diagramUrl: dUrl,
      pointList: pts.map((pt, i) => {
        const pp = photos.filter(p => p.point_id === pt.id || p.point_label === pt.label);
        return { id: pt.id, index: i + 1, label: pt.label, x_percent: pt.x_percent, y_percent: pt.y_percent,
          hasPhoto: pp.length > 0, before: pp.find(p => p.photo_type === 'before') || null, after: pp.find(p => p.photo_type === 'after') || null };
      })
    };
  }, [photos, sldData, baseUrl]);

  const curIdx    = pointList.findIndex(p => p.id === activePoint?.id);
  const canGoBack = curIdx > 0;
  const canGoFwd  = curIdx < pointList.length - 1;

  const handlePinClick = (pt) => {
    if (mapPhase !== 'visible') return;
    if (!pt.hasPhoto) return;
    setZoomOrigin(`${pt.x_percent ?? 50}% ${pt.y_percent ?? 50}%`);
    setMapPhase('zooming');
    setTimeout(() => {
      setMapPhase('hidden');
      setNavDir('fwd');
      setActivePoint(pt);
      setPhotoType(pt.before ? 'before' : 'after');
      setPhotoPhase('entering');
      if (onViewChange) onViewChange('photo');

      setIsWalking(true);
      setTimeout(() => setIsWalking(false), 1100);

      setTimeout(() => { setView('photo'); setPhotoPhase('visible'); }, 30);
    }, 650);
  };

  const handleBack = () => {
    if (photoPhase !== 'visible') return;
    setPhotoPhase('leaving');
    if (onViewChange) onViewChange('map');
    setTimeout(() => {
      setPhotoPhase('hidden');
      setView('map');
      setMapPhase('zoomback');
      setTimeout(() => setMapPhase('visible'), 600);
    }, 380);
  };

  const navigate = (idx, dir) => {
    setNavDir(dir);
    const next = pointList[idx];
    const nextType = next.before ? 'before' : 'after';
    setActivePoint(next);
    setPhotoType(nextType);

    setIsWalking(true);
    setTimeout(() => setIsWalking(false), 1100);
  };

  const mapStyle = () => {
    const base = { position:'absolute', inset:0, width:'100%', height:'100%', background:'#0D1120', overflow:'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' };
    if (mapPhase==='visible')  return { ...base, transform:'scale(1)', opacity:1, filter:'blur(0)', transition:'none' };
    if (mapPhase==='zooming')  return { ...base, transformOrigin:zoomOrigin, transform:'scale(3)', opacity:0, filter:'blur(5px)', transition:'transform 0.65s cubic-bezier(0.4,0,0.2,1), opacity 0.65s ease, filter 0.65s ease' };
    if (mapPhase==='hidden')   return { ...base, display:'none' };
    if (mapPhase==='zoomback') return { ...base, transformOrigin:zoomOrigin, transform:'scale(1)', opacity:1, filter:'blur(0)', transition:'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease 0.1s' };
    return base;
  };

  const photoStyle = () => {
    const base = { position:'absolute', inset:0, background:'#050810' };
    if (photoPhase==='hidden')   return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase==='entering') return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase==='visible')  return { ...base, opacity:1, transform:'scale(1)',    transition:'opacity 0.45s ease, transform 0.45s cubic-bezier(0.4,0,0.2,1)', pointerEvents:'all' };
    if (photoPhase==='leaving')  return { ...base, opacity:0, transform:'scale(1.05)', transition:'opacity 0.38s ease, transform 0.38s ease', pointerEvents:'none' };
    return base;
  };

  const ArrowBtn = ({ side, onClick, label }) => {
    const isH = hoveredArrow === side;
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHoveredArrow(side)}
        onMouseLeave={() => setHoveredArrow(null)}
        style={{
          position:'absolute', top:0, bottom:0, [side]:0, width:'15%', zIndex:30,
          display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
          background: isH
            ? `linear-gradient(${side==='left'?'90':'270'}deg, rgba(0,0,0,0.55) 0%, transparent 100%)`
            : `linear-gradient(${side==='left'?'90':'270'}deg, rgba(0,0,0,0.20) 0%, transparent 100%)`,
          transition:'background 0.25s ease',
        }}
      >
        <div style={{
          width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
          background: isH ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.42)',
          border:`2px solid ${isH?'#fff':'rgba(255,255,255,0.60)'}`,
          backdropFilter:'blur(6px)', color:'#fff',
          boxShadow: isH ? '0 0 28px rgba(255,255,255,0.20)' : '0 2px 14px rgba(0,0,0,0.45)',
          transform: isH ? 'scale(1.13)' : 'scale(1)',
          transition:'all 0.22s ease',
        }}>
          {side==='left'
            ? <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            : <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          }
        </div>
        {isH && (
          <div style={{ position:'absolute', bottom:28, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.82)', border:'1px solid rgba(255,255,255,0.14)', borderRadius:7, padding:'4px 11px', fontSize:11, fontWeight:700, color:'#fff', whiteSpace:'nowrap', backdropFilter:'blur(6px)', animation:'fadeUp 0.14s ease' }}>
            {side==='left' ? `← ${label}` : `${label} →`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sldw-root">
      <style>{SLD_CSS}</style>
      <div className="gta-container" style={{
        display: view === 'photo' ? 'grid' : 'block',
        gridTemplateColumns: view === 'photo' ? '1fr 300px' : '1fr',
        gap: 20,
        height: 600,
        transition: 'all 0.4s ease'
      }}>

        {/* ── MAIN AREA ── */}
        <div style={{ position:'relative', borderRadius:16, overflow:'hidden', background:'#0D1120', height:'100%', boxShadow:'0 10px 40px rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.07)' }}>

          {/* MAP PHASE */}
          <div style={mapStyle()}>
            {diagramUrl ? (
              <div className="sld-image-container">
                <img src={diagramUrl} alt="SLD" className="gta-map-img" draggable={false} />
                {pointList.map((pt) => {
                  const isH = hoveredPin === pt.id;
                  return (
                    <div key={pt.id}
                      onMouseEnter={() => setHoveredPin(pt.id)}
                      onMouseLeave={() => setHoveredPin(null)}
                      onClick={() => handlePinClick(pt)}
                      style={{ position:'absolute', left:`${pt.x_percent??50}%`, top:`${pt.y_percent??50}%`, transform:'translate(-50%,-50%)', display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor:pt.hasPhoto?'pointer':'default', zIndex:isH?30:10 }}
                    >
                      {isH && (
                        <div style={{ position:'absolute', bottom:'108%', left:'50%', transform:'translateX(-50%)', background:'rgba(8,12,24,0.92)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'5px 12px', fontSize:11, fontWeight:700, color:'#E2E8F0', whiteSpace:'nowrap', backdropFilter:'blur(8px)', boxShadow:'0 4px 16px rgba(0,0,0,0.5)', animation:'fadeUp 0.14s ease', pointerEvents:'none' }}>
                          {pt.label||`Point ${pt.index}`}{pt.hasPhoto&&<span style={{color:'#38BDF8',marginLeft:6}}>▶ View</span>}
                        </div>
                      )}
                      <div style={{ width:isH?44:34, height:isH?44:34, borderRadius:'50%', background:pt.hasPhoto?(isH?'rgba(34,211,165,0.38)':'rgba(34,211,165,0.18)'):'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.2s ease', animation:pt.hasPhoto?'pinPulse 2.5s ease-in-out infinite':'none' }}>
                        <div style={{ width:isH?30:24, height:isH?30:24, borderRadius:'50%', background:pt.hasPhoto?'#22D3A5':'rgba(255,255,255,0.28)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:'#080C18', fontFamily:'monospace', boxShadow:isH?'0 0 0 3px rgba(255,255,255,0.3),0 0 18px rgba(34,211,165,0.8)':'0 0 0 2px rgba(0,0,0,0.5),0 3px 8px rgba(0,0,0,0.4)', transition:'all 0.2s ease' }}>{pt.index}</div>
                      </div>
                      {pt.label && <div style={{ background:'rgba(8,12,24,0.88)', borderRadius:4, padding:'2px 7px', fontSize:9, fontWeight:700, color:'#E2E8F0', whiteSpace:'nowrap', border:'1px solid rgba(255,255,255,0.10)', backdropFilter:'blur(4px)', pointerEvents:'none', opacity:isH?1:0.78, transition:'opacity 0.2s' }}>{pt.label}</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,0.28)',flexDirection:'column',gap:10}}><span style={{fontSize:28}}>🗺️</span><span style={{fontWeight:600}}>No SLD Diagram uploaded</span></div>
            )}

            <div style={{ position:'absolute', top:16, left:16, color:'#38BDF8', fontSize:13, fontWeight:800, letterSpacing:1, textTransform:'uppercase', background:'rgba(15,23,42,0.88)', padding:'6px 14px', borderRadius:8, border:'1px solid rgba(56,189,248,0.30)', zIndex:10 }}>
              SLD: {siteName||'Site'}
            </div>
            <div className="sv-mobile-hint">Click a pin to view photos</div>
          </div>

          {/* PHOTO PHASE */}
          <div style={photoStyle()}>
            {activePoint && (
              <>
                <AnimatePresence initial={false} custom={navDir}>
                  <motion.img
                    key={activePoint.id + photoType}
                    src={getUrl(activePoint[photoType])}
                    alt={activePoint.label}
                    draggable={false}
                    custom={navDir}
                    variants={{
                      enter: (dir) => ({
                        scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 0.6 : 1.4),
                        opacity: 0,
                        filter: dir === 'fade' ? 'blur(10px)' : 'blur(30px)',
                        zIndex: dir === 'fwd' ? 1 : 3
                      }),
                      center: { scale: 1, opacity: 1, filter: 'blur(0px)', zIndex: 2 },
                      exit: (dir) => ({
                        scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 1.4 : 0.6),
                        opacity: 0,
                        filter: dir === 'fade' ? 'blur(10px)' : 'blur(30px)',
                        zIndex: dir === 'fwd' ? 3 : 1
                      })
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                    style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                  />
                </AnimatePresence>

                <div style={{ zIndex: 40, position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                  <button onClick={handleBack} className="sv-back-btn" style={{ pointerEvents: 'all' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                    <span>Back to Map</span>
                  </button>

                  <div style={{ position:'absolute', top:18, left:'50%', transform:'translateX(-50%)', display:'flex', gap:8, pointerEvents: 'all' }}>
                    {activePoint.before && (
                      <button onClick={()=>{setNavDir('fade');setPhotoType('before');}} style={{ padding:'7px 22px', borderRadius:20, fontSize:12, fontWeight:800, cursor:'pointer', background:photoType==='before'?'#F59E0B':'rgba(0,0,0,0.65)', color:photoType==='before'?'#000':'rgba(255,255,255,0.75)', border:`1px solid ${photoType==='before'?'#F59E0B':'rgba(255,255,255,0.2)'}`, backdropFilter:'blur(8px)', transition:'all 0.2s', boxShadow:photoType==='before'?'0 4px 15px rgba(245,158,11,0.4)':'none' }}>Before</button>
                    )}
                    {activePoint.after && (
                      <button onClick={()=>{setNavDir('fade');setPhotoType('after');}} style={{ padding:'7px 22px', borderRadius:20, fontSize:12, fontWeight:800, cursor:'pointer', background:photoType==='after'?'#22C55E':'rgba(0,0,0,0.65)', color:photoType==='after'?'#000':'rgba(255,255,255,0.75)', border:`1px solid ${photoType==='after'?'#22C55E':'rgba(255,255,255,0.2)'}`, backdropFilter:'blur(8px)', transition:'all 0.2s', boxShadow:photoType==='after'?'0 4px 15px rgba(34,197,94,0.4)':'none' }}>After</button>
                    )}
                  </div>

                  {canGoBack && <div style={{pointerEvents: 'all'}}><ArrowBtn side="left" onClick={(e)=>{e.stopPropagation();navigate(curIdx-1,'bwd');}} label={pointList[curIdx-1]?.label||`Point ${curIdx}`}/></div>}
                  {canGoFwd  && <div style={{pointerEvents: 'all'}}><ArrowBtn side="right" onClick={(e)=>{e.stopPropagation();navigate(curIdx+1,'fwd');}} label={pointList[curIdx+1]?.label||`Point ${curIdx+2}`}/></div>}

                  <div className="sv-bottom-bar" style={{pointerEvents: 'all'}}>
                    <span style={{fontWeight:800,color:'#fff',fontSize:14}}>{activePoint.label||`Point ${activePoint.index}`}</span>
                    <span style={{ fontSize:9, fontWeight:800, letterSpacing:1, padding:'3px 8px', borderRadius:4, textTransform:'uppercase', background:photoType==='before'?'rgba(245,158,11,0.25)':'rgba(34,197,94,0.25)', color:photoType==='before'?'#FCD34D':'#86EFAC' }}>{photoType}</span>
                    <span style={{fontSize:11,color:'rgba(255,255,255,0.45)',fontFamily:'monospace'}}>{curIdx+1} / {pointList.length}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── MINI MAP WIDGET (live "you are here" tracker) ── */}
        {view === 'photo' && (
          <div className="cp-fade" style={{ position: 'relative', borderRadius: 16, background: '#0D1120', border: '1px solid rgba(255,255,255,0.07)', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', top: 12, left: 12, fontSize: 10, fontWeight: 800, color: '#38BDF8', letterSpacing: 1, textTransform: 'uppercase', background: 'rgba(15,23,42,0.88)', padding: '5px 12px', borderRadius: 6, zIndex: 10, backdropFilter: 'blur(4px)', border: '1px solid rgba(56,189,248,0.2)' }}>
              📍 Live Tracking
            </div>

            {diagramUrl ? (
              <div className="sld-image-container" style={{ transform: 'scale(0.85)' }}>
                <img src={diagramUrl} alt="SLD" className="gta-map-img" draggable={false} style={{ opacity: 0.6 }} />

                {pointList.map(pt => (
                  <div key={pt.id} style={{ position: 'absolute', left: `${pt.x_percent}%`, top: `${pt.y_percent}%`, transform: 'translate(-50%,-50%)', width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
                ))}

                {activePoint && (
                  <motion.div
                    animate={{ left: `${activePoint.x_percent}%`, top: `${activePoint.y_percent}%` }}
                    transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                    style={{ position: 'absolute', transform: 'translate(-50%, -100%)', zIndex: 20 }}
                  >
                    <div style={{
                      background: 'rgba(15,23,42,0.8)', padding: '6px', borderRadius: '50%',
                      border: '2px solid #38BDF8', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', boxShadow: '0 0 20px rgba(56,189,248,0.9)',
                      backdropFilter: 'blur(4px)',
                      animation: isWalking ? 'bobWalk 0.4s infinite alternate ease-in-out' : 'none'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="5" r="2.5"></circle>
                        <line x1="12" y1="7.5" x2="12" y2="14"></line>
                        <line x1="12" y1="9" x2="12" y2="16" style={{ transformOrigin: '12px 9px', transform: isWalking ? 'none' : 'rotate(-20deg)', animation: isWalking ? 'swingArm 0.4s infinite alternate-reverse ease-in-out' : 'none' }}></line>
                        <line x1="12" y1="9" x2="12" y2="16" style={{ transformOrigin: '12px 9px', transform: isWalking ? 'none' : 'rotate(20deg)', animation: isWalking ? 'swingArm 0.4s infinite alternate ease-in-out' : 'none' }}></line>
                        <line x1="12" y1="14" x2="12" y2="22" style={{ transformOrigin: '12px 14px', transform: isWalking ? 'none' : 'rotate(-20deg)', animation: isWalking ? 'swingLeg 0.4s infinite alternate ease-in-out' : 'none' }}></line>
                        <line x1="12" y1="14" x2="12" y2="22" style={{ transformOrigin: '12px 14px', transform: isWalking ? 'none' : 'rotate(20deg)', animation: isWalking ? 'swingLeg 0.4s infinite alternate-reverse ease-in-out' : 'none' }}></line>
                      </svg>
                    </div>
                    <div style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', background: '#38BDF8', color: '#000', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                      You are here
                    </div>
                  </motion.div>
                )}
              </div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 600 }}>No Diagram Uploaded</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SLDWalkthrough;
